/**
 * Supplier enrichment hooks — Phase A.
 *
 * Thin wrappers over the new tRPC procedures for categories, SKU links,
 * pricing, and the cross-feature taxonomy table. Cards consume these
 * directly; cache invalidation flows through tRPC utils.
 */
import { trpc } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';

type TaxonomyKind =
  | 'supplier_category'
  | 'dye_method'
  | 'wash'
  | 'embellishment'
  | 'doc_type'
  | 'trim_type';

export function useSupplierCategories(supplierId: string | undefined) {
  const { organization } = useOrganization();
  return trpc.suppliers.listCategories.useQuery(
    { supplierId: supplierId ?? '' },
    { enabled: Boolean(supplierId && organization?.id), staleTime: 30_000 },
  );
}

export function useSupplierSkus(supplierId: string | undefined) {
  const { organization } = useOrganization();
  return trpc.suppliers.listSkus.useQuery(
    { supplierId: supplierId ?? '' },
    { enabled: Boolean(supplierId && organization?.id), staleTime: 30_000 },
  );
}

export function useSupplierPricing(supplierId: string | undefined) {
  const { organization } = useOrganization();
  return trpc.suppliers.listPricing.useQuery(
    { supplierId: supplierId ?? '' },
    { enabled: Boolean(supplierId && organization?.id), staleTime: 30_000 },
  );
}

export function useTaxonomy(kind: TaxonomyKind) {
  const { organization } = useOrganization();
  return trpc.suppliers.listTaxonomy.useQuery(
    { kind },
    { enabled: Boolean(organization?.id), staleTime: 5 * 60_000 },
  );
}

/**
 * Centralised mutation hooks. Each invalidates the relevant query so the
 * cards re-render immediately after a save.
 */
export function useSupplierEnrichmentMutations() {
  const utils = trpc.useUtils();

  const setCategories = trpc.suppliers.setCategories.useMutation({
    onSuccess: (_data, vars) =>
      utils.suppliers.listCategories.invalidate({ supplierId: vars.supplierId }),
  });

  const setSkus = trpc.suppliers.setSkus.useMutation({
    onSuccess: (_data, vars) =>
      utils.suppliers.listSkus.invalidate({ supplierId: vars.supplierId }),
  });

  const upsertPricing = trpc.suppliers.upsertPricing.useMutation({
    onSuccess: (_data, vars) =>
      utils.suppliers.listPricing.invalidate({ supplierId: vars.supplierId }),
  });

  const deletePricing = trpc.suppliers.deletePricing.useMutation({
    onSuccess: () => utils.suppliers.listPricing.invalidate(),
  });

  const addTaxonomyValue = trpc.suppliers.addTaxonomyValue.useMutation({
    onSuccess: (_data, vars) =>
      utils.suppliers.listTaxonomy.invalidate({ kind: vars.kind }),
  });

  return {
    setCategories,
    setSkus,
    upsertPricing,
    deletePricing,
    addTaxonomyValue,
  };
}
